import * as React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faEnvelope,
  faTimesCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { useFriends } from "../../hooks/useFriends";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { Loader } from "../Loader";
import classNames from "classnames";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@reach/tabs";
import { FriendsQueryQuery } from "../../graphql/FriendsQuery.generated";
import { ProfileImage } from "../ProfileImage";
import { useIntersection } from "../../hooks/useIntersection";
import { TabValue, usePlayerProfile } from "../PlayerProfile";
import isEqual from "react-fast-compare";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import styles from "./Friends.module.scss";
import { Intent, JoinTable } from "../JoinTable";
import { GAME_IDS } from "../Games";
import { ButtonWithSound } from "../AudioProvider";
import { useScreenType } from "../../hooks/useScreenType";
import {
  RIGHT_BAR_MOBILE_SIZE,
  RIGHT_BAR_TABLET_SIZE,
} from "../Bar/RightBar/Bar";
import { useCustomChatContext } from "../UserProvider";
import { useMutation } from "../../hooks/useMutation";
import {
  CreateFriendsChatMutationMutation,
  CreateFriendsChatMutationMutationVariables,
} from "../../graphql/CreateFriendsChatMutation.generated";

import { loader } from "graphql.macro";

const CREATE_FRIENDS_CHAT_MUTATION = loader(
  "../../graphql/CreateFriendsChatMutation.graphql"
);

type NodesType = Array<
  NonNullable<FriendsQueryQuery["me"]>["friends"]["edges"][0]["node"]
>;
const FriendsContext = React.createContext<(open: boolean) => void>(() => {
  throw new Error("Provider not found");
});

const TABS = ["all", "online"];

const AVATAR_WIDTH = 46;
const AVATAR_HEIGHT = 46;
const Friend = React.memo<{
  friend: NodesType[0];
  container: React.MutableRefObject<HTMLDivElement | null>;
  closePanel: () => void;
}>(({ friend, container, closePanel }) => {
  const openPlayerProfile = usePlayerProfile();
  const { setChatUserId, setOpenedChat } = useCustomChatContext();
  const target = React.useRef<HTMLDivElement>(null);
  const intersecting = useIntersection(target, {
    root: container,
    once: true,
    defaultIntersecting: false,
  });
  const { t } = useTranslation();

  const [{ fetching }, createChat] = useMutation<
    CreateFriendsChatMutationMutation,
    CreateFriendsChatMutationMutationVariables
  >(CREATE_FRIENDS_CHAT_MUTATION);

  const handleOpenChat = () => {
    createChat({ input: { playerId: friend.userB.id } }).then((data) => {
      if (data?.data?.getOrCreateDirectChat?.chatId) {
        setOpenedChat(true);
        setChatUserId(data?.data?.getOrCreateDirectChat?.chatId);
        closePanel();
      }
    });
  };

  const openProfile = (tab?: TabValue) =>
    openPlayerProfile({
      ...friend.userB,
      games: GAME_IDS,
      tournament: false,
      tab,
    });
  const activeTable = friend.userB.activeTables[0];
  return (
    <div ref={target} className="bg-blue-900 rounded p-2">
      <div className="overflow-hidden">
        <div className="flex -mx-1 items-center">
          <div className="flex-shrink-0 px-1">
            <div
              className="overflow-hidden bg-gray-200 rounded-full"
              style={{ width: AVATAR_WIDTH, height: AVATAR_HEIGHT }}
            >
              {intersecting && (
                <button className="block" onClick={() => openProfile()}>
                  <ProfileImage
                    name={friend.userB.name}
                    id={friend.userB.id}
                    width={AVATAR_WIDTH}
                    height={AVATAR_HEIGHT}
                  />
                </button>
              )}
            </div>
          </div>
          <div className="px-1 whitespace-normal flex-1 text-truncate">
            <button className="block" onClick={() => openProfile()}>
              <strong>{friend.userB.name}</strong>
            </button>
            <div
              className={classNames("text-sm", "leading-5", {
                "text-red-500":
                  !friend.userB.presence && activeTable === undefined,
                "text-green-500": friend.userB.presence,
              })}
            >
              {activeTable !== undefined
                ? t("in_table")
                : friend.userB.presence
                ? t("online")
                : t("offline")}
            </div>
          </div>
          <div className="px-1 flex-shrink-0 flex -mx-1">
            {activeTable !== undefined && (
              <JoinTable table={activeTable} intent={Intent.Friend}>
                {(disabled, full, join) => {
                  return (
                    !disabled && (
                      <div className="px-1">
                        <ButtonWithSound
                          className={classNames(
                            "btn",
                            "rounded",
                            "btn-sm",
                            full ? "btn-orange-600" : "btn-green-600"
                          )}
                          onClick={() => {
                            closePanel();
                            join();
                          }}
                        >
                          <FontAwesomeIcon icon={faArrowRight} fixedWidth />{" "}
                          {t(full ? "watch" : "join")}
                        </ButtonWithSound>
                      </div>
                    )
                  );
                }}
              </JoinTable>
            )}

            <div className="px-1">
              <ButtonWithSound
                className="btn btn-blue-600 rounded btn-sm"
                onClick={handleOpenChat}
              >
                <FontAwesomeIcon icon={faEnvelope} fixedWidth /> {t("message")}
              </ButtonWithSound>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}, isEqual);

const Friends = ({
  friends,
  loadMore,
  closePanel,
}: {
  friends: NodesType;
  loadMore?: () => Promise<unknown>;
  closePanel: () => void;
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const loadingRef = useInfiniteScroll<HTMLDivElement>(
    { root: containerRef, rootMargin: "100px" },
    () => {
      if (loadMore !== undefined) return loadMore();
      else return Promise.resolve();
    }
  );
  const { t } = useTranslation();
  if (friends.length === 0 && loadMore === undefined)
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 relative">
          <AbsoluteCenter>
            <div className="p-4 text-center">
              <span className="text-white font-bold">
                {t("no_friends_available")}
              </span>
            </div>
          </AbsoluteCenter>
        </div>
      </div>
    );
  else
    return (
      <div className="flex flex-col h-full">
        <div
          className={classNames("flex-1", "overflow-auto", styles.scrollable)}
          ref={containerRef}
        >
          {friends.map((friend) => (
            <div
              className={classNames("w-full mb-4", styles.friend)}
              key={friend.userB.id}
            >
              <Friend
                friend={friend}
                container={containerRef}
                closePanel={closePanel}
              />
            </div>
          ))}
          {loadMore !== undefined && (
            <div ref={loadingRef} className="py-2">
              <Loader description={""} />
            </div>
          )}
        </div>
      </div>
    );
};

const FriendPlaceHolder = ({
  friends,
  children,
}: React.PropsWithChildren<{
  friends: NonNullable<FriendsQueryQuery["me"]>["friends"] | undefined;
}>) => {
  const { t } = useTranslation();
  if (friends === undefined)
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 relative">
          <AbsoluteCenter>
            <Loader description={t("loading_friends")} />
          </AbsoluteCenter>
        </div>
      </div>
    );
  else if (friends.edges.length === 0)
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 relative">
          <AbsoluteCenter>
            <div className="p-4 text-center">
              <span className="text-white font-bold">
                {t("no_friends_available")}
              </span>
            </div>
          </AbsoluteCenter>
        </div>
      </div>
    );
  else return <>{children}</>;
};
const FriendsContent = ({ onDismiss }: { onDismiss: () => void }) => {
  const { friends, fetchMore } = useFriends();
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = React.useState(0);

  const loadMore = friends?.pageInfo?.hasNextPage
    ? () => {
        const endCursor = friends?.pageInfo?.endCursor ?? null;
        if (endCursor === null) return Promise.reject();
        return fetchMore(endCursor);
      }
    : undefined;

  const [all, online] = React.useMemo(() => {
    const all: NodesType = [];
    const online: NodesType = [];
    for (const edge of friends?.edges ?? []) {
      all.push(edge.node);
      if (edge.node.userB.presence) online.push(edge.node);
    }

    return [all, online];
  }, [friends]);

  return (
    <Tabs
      className="h-full overflow-hidden"
      index={tabIndex}
      onChange={setTabIndex}
    >
      <div className="flex flex-col h-full text-white">
        <div
          className={classNames(
            "flex",
            "items-center",
            "flex-shrink-0",
            styles.header
          )}
        >
          <TabList className="flex-1">
            {TABS.map((tab, index) => (
              <Tab
                key={index}
                className={classNames(
                  "px-4",
                  "py-3",
                  "w-1/2",
                  "border-r",
                  "border-black",
                  styles.headerButton,
                  {
                    [styles.active]: index === tabIndex,
                  }
                )}
              >
                {t(tab)}
              </Tab>
            ))}
          </TabList>
          <div className="flex-shrink-0 px-4">
            <button onClick={onDismiss}>
              <FontAwesomeIcon icon={faTimesCircle} size="lg" />
            </button>
          </div>
        </div>
        <TabPanels className="flex-1 overflow-hidden">
          <TabPanel className="h-full p-4">
            <FriendPlaceHolder friends={friends}>
              <Friends
                friends={all}
                loadMore={loadMore}
                closePanel={onDismiss}
              />
            </FriendPlaceHolder>
          </TabPanel>
          <TabPanel className="h-full p-4">
            <FriendPlaceHolder friends={friends}>
              <Friends
                friends={online}
                loadMore={loadMore}
                closePanel={onDismiss}
              />
            </FriendPlaceHolder>
          </TabPanel>
        </TabPanels>
      </div>
    </Tabs>
  );
};

const Popup = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();
  const screenType = useScreenType();
  const stylesMap = {
    DESKTOP: { bottom: 0, left: 0 },
    TABLET: { top: 0, right: 0, ...RIGHT_BAR_TABLET_SIZE },
    MOBILE: { top: 0, right: 0, ...RIGHT_BAR_MOBILE_SIZE },
  };

  const adjustedStyle = React.useMemo(() => stylesMap[screenType] || {}, [
    screenType,
  ]);

  return (
    <>
      <div
        className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-20"
        onClick={close}
      />
      <div
        className="w-1/3 h-9/10 absolute shadow-2xl bg-blue-800 z-30"
        style={{ ...adjustedStyle }}
        aria-label={t("friends")}
      >
        <FriendsContent onDismiss={close} />
      </div>
    </>
  );
};

export const FriendsPanelProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const close = React.useCallback(() => setOpen(false), []);

  return (
    <FriendsContext.Provider value={setOpen}>
      {children}
      {isOpen && <Popup close={close} />}
    </FriendsContext.Provider>
  );
};

export const useFriendsPanel = () => {
  return React.useContext(FriendsContext);
};
